import React, { useEffect } from "react";
import {
  Avatar,
  Box,
  ButtonBase,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { LockOutlined, WarningAmberOutlined } from "@mui/icons-material";
import { useAppSelector } from "redux/hooks";
import {
  selectEmail,
  selectProfileImage,
  selectUserName,
} from "redux/authSlice";
import { getNameInitials } from "helpers/methods";
import history from "utils/history";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import { useSetPageHeader } from "global/components/PageHeader/PageHeaderContext";
import { useFeatures } from "hooks/use-feature";
import strings from "global/constants/StringConstants";
import settingsShellTokens, {
  PHONE_SM_QUERY,
  settingsShellResponsiveTokens,
} from "screens/Settings/Settings.tokens";
import useSettingsNavSections, {
  SettingsNavItem,
} from "screens/Settings/components/SettingsNavSections";
import { NavChevronIcon } from "screens/Settings/components/SettingsNavIcons";

const { colors, layout } = settingsShellTokens;
const { phone } = settingsShellResponsiveTokens;

/**
 * The phone-only Settings home: a "who am I" card over the same PERSONAL /
 * ORGANIZATION groups the rail carries on wider viewports, each row a full-width
 * tap target with a trailing chevron.
 *
 * `/settings` has no desktop counterpart — the reference opens straight into a
 * section there — so anything at `sm` and up is redirected to the first section
 * the user may reach.
 */
const SettingsIndex: React.FC = () => {
  useTitle(title.SETTINGS);
  const isPhone = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const sections = useSettingsNavSections();
  const { state: featureState } = useFeatures();
  const userName = useAppSelector(selectUserName);
  const userEmail = useAppSelector(selectEmail);
  const profileImage = useAppSelector(selectProfileImage);

  useSetPageHeader(isPhone ? "Settings" : undefined);

  const firstPath = sections[0]?.items[0]?.path;

  useEffect(() => {
    if (!isPhone && firstPath) {
      history.replace(firstPath);
    }
  }, [isPhone, firstPath]);

  const getImageUrl = () => {
    if (!profileImage || profileImage === "data:image/png;base64,") {
      return "";
    }
    return profileImage;
  };

  if (!isPhone) {
    return null;
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        // Same page canvas as every other Settings screen (see SettingsLayout).
        bgcolor: colors.canvas,
        minHeight: `calc(100vh - ${layout.navStickyTop}px)`,
        p: phone.indexPadding.std,
        [PHONE_SM_QUERY]: { padding: phone.indexPadding.sm },
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: phone.userCard.gap.std,
          bgcolor: phone.userCard.bg,
          border: `1px solid ${phone.userCard.border}`,
          borderRadius: phone.userCard.radius,
          p: phone.userCard.padding.std,
          mb: phone.userCard.marginBottom.std,
          [PHONE_SM_QUERY]: {
            gap: phone.userCard.gap.sm,
            padding: phone.userCard.padding.sm,
            marginBottom: phone.userCard.marginBottom.sm,
          },
        }}
      >
        <Avatar
          src={getImageUrl()}
          alt={userName || "User"}
          sx={{
            width: phone.userCard.avatarSize.std,
            height: phone.userCard.avatarSize.std,
            flexShrink: 0,
            bgcolor: colors.avatarBg,
            color: colors.avatarText,
            fontSize: phone.userCard.avatarFontSize.std,
            fontWeight: 700,
            [PHONE_SM_QUERY]: {
              width: phone.userCard.avatarSize.sm,
              height: phone.userCard.avatarSize.sm,
              fontSize: phone.userCard.avatarFontSize.sm,
            },
          }}
        >
          {getNameInitials(userName)}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="div"
            noWrap
            sx={{
              fontSize: phone.userCard.nameFontSize.std,
              fontWeight: 700,
              color: colors.userName,
              lineHeight: "normal",
              [PHONE_SM_QUERY]: { fontSize: phone.userCard.nameFontSize.sm },
            }}
          >
            {userName}
          </Typography>
          <Typography
            component="div"
            noWrap
            sx={{
              fontSize: phone.userCard.emailFontSize.std,
              color: colors.userEmail,
              lineHeight: "normal",
              [PHONE_SM_QUERY]: { fontSize: phone.userCard.emailFontSize.sm },
            }}
          >
            {userEmail}
          </Typography>
        </Box>
      </Box>

      <Box component="nav" aria-label="Settings">
        {sections.map((section, secIdx) => (
          <Box
            key={section.title}
            sx={{
              mb:
                secIdx === sections.length - 1
                  ? 0
                  : phone.groupMarginBottom.std,
              [PHONE_SM_QUERY]: {
                marginBottom:
                  secIdx === sections.length - 1
                    ? 0
                    : phone.groupMarginBottom.sm,
              },
            }}
          >
            <Typography
              component="div"
              sx={{
                textTransform: "uppercase",
                fontSize: phone.sectionLabel.fontSize.std,
                fontWeight: 700,
                letterSpacing: phone.sectionLabel.letterSpacing,
                lineHeight: "normal",
                color: colors.sectionLabel,
                p: phone.sectionLabel.padding,
                [PHONE_SM_QUERY]: {
                  fontSize: phone.sectionLabel.fontSize.sm,
                },
              }}
            >
              {section.title}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              {section.items.map((item: SettingsNavItem) => {
                const gate = item.featureKey
                  ? featureState(item.featureKey)
                  : undefined;
                const isGated =
                  !!gate && gate.status !== strings.FEATURE_STATUS_AVAILABLE;
                const gateTooltip = isGated
                  ? gate!.status === strings.FEATURE_STATUS_NOT_IN_PLAN
                    ? strings.FEATURE_GATE_UPSELL_TOOLTIP
                    : ((gate as any).disabledMessage ??
                      strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP)
                  : "";

                const row = (
                  <ButtonBase
                    key={item.name}
                    disableRipple
                    onClick={() => history.push(item.path)}
                    sx={{
                      display: "flex",
                      justifyContent: "flex-start",
                      alignItems: "center",
                      width: "100%",
                      gap: phone.row.gap.std,
                      p: phone.row.padding.std,
                      borderRadius: phone.row.radius,
                      fontFamily: "inherit",
                      fontSize: phone.row.labelFontSize.std,
                      fontWeight: phone.row.labelFontWeight,
                      color: isGated ? colors.sectionLabel : phone.row.labelColor,
                      textAlign: "left",
                      "&:hover": { bgcolor: colors.itemBgHover },
                      "& .MuiSvgIcon-root": {
                        width: phone.row.iconSize.std,
                        height: phone.row.iconSize.std,
                        strokeWidth: phone.row.iconStrokeWidth,
                      },
                      [PHONE_SM_QUERY]: {
                        gap: phone.row.gap.sm,
                        padding: phone.row.padding.sm,
                        fontSize: phone.row.labelFontSize.sm,
                        "& .MuiSvgIcon-root": {
                          width: phone.row.iconSize.sm,
                          height: phone.row.iconSize.sm,
                        },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexShrink: 0,
                        color: isGated ? colors.sectionLabel : colors.itemText,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.name}
                    </Box>
                    {isGated &&
                      (gate!.status === strings.FEATURE_STATUS_ADMIN_DISABLED ? (
                        <WarningAmberOutlined
                          sx={{ fontSize: 15, opacity: 0.65, flexShrink: 0 }}
                        />
                      ) : (
                        <LockOutlined
                          sx={{ fontSize: 15, opacity: 0.65, flexShrink: 0 }}
                        />
                      ))}
                    <NavChevronIcon
                      sx={{
                        color: phone.row.chevronColor,
                        // The row rule above sizes every glyph inside it; the
                        // chevron is drawn a little smaller than the leading icon.
                        "&&": {
                          width: phone.row.chevronSize.std,
                          height: phone.row.chevronSize.std,
                          [PHONE_SM_QUERY]: {
                            width: phone.row.chevronSize.sm,
                            height: phone.row.chevronSize.sm,
                          },
                        },
                      }}
                    />
                  </ButtonBase>
                );

                if (!isGated) return row;

                return (
                  <Tooltip key={item.name} title={gateTooltip} placement="top" arrow>
                    <Box component="span" sx={{ display: "block" }}>
                      {row}
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default SettingsIndex;
